/**
 * A aba "Prontuário" do cliente (#811, ADR-0022): o resumo de cada
 * atendimento, no desenho do plano "Clínica no CliqCRM".
 *
 * ── Integrar, e não construir ────────────────────────────────────────────
 *
 * O prontuário fica no sistema da clínica. A aba mostra uma linha do tempo
 * com um ponto por atendimento: a data e a hora, quem atendeu e o tipo. Em
 * cima, o aviso de onde o registro completo está, no lugar da trava da
 * "camada 2" do plano: aqui não há evolução, CID nem prescrição para mostrar.
 *
 * ── Só para quem tem a marca ─────────────────────────────────────────────
 *
 * A aba só existe para quem tem "acesso ao prontuário" (`/v1/me`), dado pelo
 * administrador em Configurações > Pessoas. A API confere de novo: sem a
 * marca, 403.
 */

import type { ReactNode } from 'react';
import type { AtendimentoDoProntuario, ProntuarioDoPaciente } from '@cliqcrm/contracts';
import { useProntuarioDoPaciente } from '@/shared/api/consultas';
import { Icone } from '@/shared/ui';
import { Carregando, Erro } from '@/shared/ui/estados/estados';
import './aba-do-prontuario.css';

/** "24/09/2026 09:00" no fuso de quem olha. */
function quando(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Por que a lista vem curta (#1144). O histórico é lido agora do SISAC, pelo
 * paciente vinculado ao cliente.
 */
const RECADO_DO_SISAC: Readonly<Record<Exclude<ProntuarioDoPaciente['sisac'], 'ok'>, string>> = {
  sem_vinculo:
    'Este cliente ainda não está vinculado a um paciente do SISAC. No atendimento, em ' +
    '"Paciente no SISAC", clique em "Procurar no SISAC" e vincule o paciente: o histórico ' +
    'aparece aqui.',
  sem_partes:
    'A agenda da clínica desta organização não tem as partes do histórico ligadas ' +
    '("Consultas do paciente" e "Exames do paciente"). Quem configura a integração liga em ' +
    'Automações > Integrações.',
  fora: 'O SISAC não respondeu agora. Abra a aba de novo daqui a pouco.',
  parcial:
    'O SISAC respondeu só em parte: uma das leituras falhou, e a lista pode estar incompleta.',
};

/** O nome do sistema de origem, como a clínica o conhece. */
function nomeDaOrigem(origem: string): string {
  return origem === 'sisac' ? 'SISAC' : origem;
}

export function ProntuarioNoCliente({ clienteId }: { clienteId: string }): ReactNode {
  const consulta = useProntuarioDoPaciente(clienteId);

  if (consulta.isPending) {
    return <Carregando forma="lista" linhas={3} rotulo="Carregando o prontuário" />;
  }
  if (consulta.isError) {
    return (
      <Erro
        erro={consulta.error}
        titulo="Não foi possível abrir o prontuário"
        aoRepetir={() => void consulta.refetch()}
      />
    );
  }

  const atendimentos = consulta.data.itens;
  return (
    <div className="pront-cli">
      <div className="pront-cli__aviso">
        <Icone nome="documento" />
        <p>
          <b>O prontuário completo fica no sistema da clínica.</b> Aqui aparece só o resumo de cada
          atendimento: quando, quem atendeu e o tipo. Cada abertura desta aba fica registrada.
        </p>
      </div>

      {consulta.data.sisac !== 'ok' && (
        <p className="pront-cli__vazio" role="status">
          {RECADO_DO_SISAC[consulta.data.sisac]}
        </p>
      )}
      {atendimentos.length === 0 ? (
        consulta.data.sisac === 'ok' && (
          <p className="pront-cli__vazio">
            Nenhum atendimento deste paciente no sistema da clínica.
          </p>
        )
      ) : (
        <ol className="pront-cli__linha" aria-label="Atendimentos do paciente">
          {atendimentos.map((atendimento) => (
            <Atendimento key={atendimento.id} atendimento={atendimento} />
          ))}
        </ol>
      )}
    </div>
  );
}

function Atendimento({ atendimento }: { atendimento: AtendimentoDoProntuario }): ReactNode {
  return (
    <li className="pront-cli__evol">
      <span className="pront-cli__meta">
        {[quando(atendimento.realizado_em), atendimento.profissional]
          .filter((parte): parte is string => parte !== null)
          .join(' · ')}
      </span>
      <span className="pront-cli__tipo">{atendimento.tipo}</span>
      <span className="pront-cli__origem">
        Registro {atendimento.codigo} no {nomeDaOrigem(atendimento.origem)}
      </span>
    </li>
  );
}
